/**
 * TeamSmsToggleCard — v2.5.135 SMS-A
 * ─────────────────────────────────────────────────────────────────
 * Manager-only card for turning SMS sending on/off for a team.
 * Mounted inside TeamPage's chat section.
 *
 * Backend: GET/PATCH /api/teams/:teamId/sms-settings.
 *
 * The toggle is gated by THREE things in production:
 *   1. Anthropic admin must have configured a provider (Superman)
 *   2. The team manager flips this toggle ON
 *   3. Each user must have a paid Messaging subscription (SMS-B)
 *
 * For SMS-A v1 #3 is admin-comped; the manager just sees the
 * team-side toggle.
 */

import { useEffect, useState } from 'react';
import { MessageSquare, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api.js';

export default function TeamSmsToggleCard({ teamId }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/teams/${teamId}/sms-settings`)
      .then(({ data }) => { if (!cancelled) setSettings(data.settings); })
      .catch(() => { if (!cancelled) setSettings(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [teamId]);

  async function setEnabled(next) {
    if (saving) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/teams/${teamId}/sms-settings`, {
        is_enabled: next,
        hourly_send_cap: settings?.hourly_send_cap ?? null,
      });
      setSettings(data.settings);
      toast.success(next ? 'SMS turned on for this team' : 'SMS turned off');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to update SMS setting');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="card mb-5">
        <div className="text-secondary text-xs">Loading SMS settings…</div>
      </div>
    );
  }

  const isEnabled = !!settings?.is_enabled;

  return (
    <div className="card mb-5">
      <div className="flex items-center gap-2 mb-2">
        <MessageSquare size={16} style={{ color: 'var(--ice)' }} />
        <h3 className="font-semibold">SMS messaging</h3>
      </div>
      <p className="text-secondary text-xs mb-4">
        Allow this team to send game reminders + announcements via SMS in addition to email and push notifications.
        Each player who wants to receive SMS must have a verified phone and an active Messaging subscription.
      </p>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: 14,
        background: 'var(--surface-2)',
        border: `1px solid var(--border)`,
        borderRadius: 10,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <strong style={{ fontSize: 14 }}>
              {isEnabled ? 'SMS sending is ON for this team' : 'SMS sending is OFF for this team'}
            </strong>
            {isEnabled && <CheckCircle2 size={14} color="var(--goal)" />}
          </div>
          {isEnabled && settings.enabled_at && (
            <div className="text-secondary text-xs" style={{ marginTop: 4 }}>
              Turned on {new Date(settings.enabled_at).toLocaleDateString()}.
            </div>
          )}
          {!isEnabled && (
            <div className="text-secondary text-xs" style={{ marginTop: 4 }}>
              When OFF, no SMS will be sent for this team regardless of individual player preferences.
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => setEnabled(!isEnabled)}
          disabled={saving}
          style={{
            padding: '7px 16px',
            background: isEnabled ? 'transparent' : 'var(--ice)',
            color: isEnabled ? 'var(--fire)' : '#000',
            border: `1px solid ${isEnabled ? 'var(--fire)' : 'var(--ice)'}`,
            borderRadius: 8,
            fontWeight: 700,
            fontSize: 13,
            cursor: saving ? 'wait' : 'pointer',
            whiteSpace: 'nowrap',
          }}>
          {saving ? '…' : (isEnabled ? 'Turn off' : 'Turn on')}
        </button>
      </div>

      {/* When enabled, show a small heads-up about what still has to be true */}
      {isEnabled && (
        <div style={{
          marginTop: 10, padding: 10,
          background: 'var(--surface-2)',
          border: `1px solid var(--border)`,
          borderRadius: 8,
          display: 'flex', alignItems: 'flex-start', gap: 8,
        }}>
          <AlertCircle size={14} color="var(--ice)" style={{ marginTop: 2, flexShrink: 0 }} />
          <div className="text-xs text-secondary">
            Players will only receive SMS if they:
            (1) have added and verified their phone in their profile,
            (2) opted in to SMS notifications, and
            (3) have an active Messaging subscription.
          </div>
        </div>
      )}
    </div>
  );
}
